import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import UserAvatar from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Users, Trash2, Pencil, ArrowRightLeft, Power, PowerOff, MapPin } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#3B82F6", "#8B5CF6", "#F59E0B", "#10B981", "#EF4444", "#EC4899", "#06B6D4"];

function CenterDialog({ open, onOpenChange, center, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({});
  const [capturing, setCapturing] = useState(false);
  useEffect(() => { if (open) setForm(center || { name: "", contact_first_name: "", contact_last_name: "", type: "RI", address: "", color: "#3B82F6", owner_first_name: "", owner_last_name: "", owner_email: "", owner_password: "", geo_enabled: false, geo_radius: 150, pay_period_type: "weekly", pay_week_start_day: 0 }); }, [open, center]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const capture = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, latitude: p.coords.latitude, longitude: p.coords.longitude })); setCapturing(false); toast.success(t("locationCaptured")); },
      () => { setCapturing(false); toast.error(t("locationDenied")); },
      { enableHighAccuracy: true, timeout: 9000 });
  };
  const save = async () => {
    if (!form.contact_first_name?.trim() || !form.contact_last_name?.trim()) {
      toast.error(`${t("contactFirstName")} / ${t("contactLastName")}`);
      return;
    }
    try {
      if (center) await api.put(`/centers/${center.id}`, form); else await api.post("/centers", form);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const days = [t("sunday"), t("monday"), t("tuesday"), t("wednesday"), t("thursday"), t("friday"), t("saturday")];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle>{center ? t("editCenter") : t("addCenter")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("centerName")}</Label><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="center-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("contactFirstName")}</Label><Input value={form.contact_first_name || ""} onChange={(e) => set("contact_first_name", e.target.value)} data-testid="center-contact-first-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("contactLastName")}</Label><Input value={form.contact_last_name || ""} onChange={(e) => set("contact_last_name", e.target.value)} data-testid="center-contact-last-name" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("centerType")}</Label>
            <Select value={form.type || "RI"} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="center-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="RI">RI</SelectItem>
                <SelectItem value="RTF">RTF</SelectItem>
                <SelectItem value="RPA">RPA</SelectItem>
                <SelectItem value="Autre">{t("centerTypeOther")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("address")}</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="center-address" /></div>

          {!center && (
            <div className="pt-3 border-t border-border space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{t("ownerAccount")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">{t("firstName")}</Label><Input value={form.owner_first_name || ""} onChange={(e) => set("owner_first_name", e.target.value)} data-testid="center-owner-first-name" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("lastName")}</Label><Input value={form.owner_last_name || ""} onChange={(e) => set("owner_last_name", e.target.value)} data-testid="center-owner-last-name" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">{t("email")}</Label><Input type="email" value={form.owner_email || ""} onChange={(e) => set("owner_email", e.target.value)} data-testid="center-owner-email" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("password")}</Label><Input type="password" value={form.owner_password || ""} onChange={(e) => set("owner_password", e.target.value)} data-testid="center-owner-password" /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label className="text-xs">{t("color")}</Label>
            <div className="flex gap-2">{COLORS.map((c) => <button key={c} onClick={() => set("color", c)} style={{ background: c }} className={`w-7 h-7 rounded-full ${form.color === c ? "ring-2 ring-offset-2 ring-foreground" : ""}`} />)}</div>
          </div>

          {center && (
            <>
              <div className="pt-3 border-t border-border space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{t("geolocationSettings")}</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="w-4 h-4" checked={!!form.geo_enabled} onChange={(e) => set("geo_enabled", e.target.checked)} data-testid="geo-enabled" />
                  {t("requireGeo")}
                </label>
                {form.geo_enabled && (
                  <>
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={capture} disabled={capturing} data-testid="capture-location">
                        <MapPin className="w-4 h-4 mr-1.5" />{capturing ? t("loading") : t("captureLocation")}
                      </Button>
                      {form.latitude != null && <span className="text-xs text-emerald-600" data-testid="location-set">✓ {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}</span>}
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">{t("radiusMeters")}</Label>
                      <Input type="number" value={form.geo_radius ?? 150} onChange={(e) => set("geo_radius", parseInt(e.target.value) || 0)} data-testid="geo-radius" /></div>
                  </>
                )}
              </div>

              <div className="pt-3 border-t border-border space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("payPeriodType")}</p>
                <Select value={form.pay_period_type || "weekly"} onValueChange={(v) => set("pay_period_type", v)}>
                  <SelectTrigger data-testid="pay-period-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="weekly">{t("weekly")}</SelectItem><SelectItem value="biweekly">{t("biweekly")}</SelectItem></SelectContent>
                </Select>
                <div className="space-y-1.5"><Label className="text-xs">{t("weekStartsOn")}</Label>
                  <Select value={String(form.pay_week_start_day ?? 0)} onValueChange={(v) => set("pay_week_start_day", parseInt(v))}>
                    <SelectTrigger data-testid="week-start-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{days.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.pay_period_type === "biweekly" && (
                  <div className="space-y-1.5"><Label className="text-xs">{t("anchorDate")}</Label>
                    <Input type="date" value={form.pay_anchor_date || ""} onChange={(e) => set("pay_anchor_date", e.target.value)} data-testid="anchor-date" /></div>
                )}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={save} data-testid="save-center">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({ open, onOpenChange, center }) {
  const { t } = useT();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ email: "", role: "employee", name: "", password: "", position: "" });
  const [adding, setAdding] = useState(false);

  const load = () => api.get(`/centers/${center.id}/members`).then((r) => setMembers(r.data));
  useEffect(() => { if (open && center) { load(); setForm({ email: "", role: "employee", name: "", password: "", position: "" }); setAdding(false); } }, [open, center]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    try {
      await api.post(`/centers/${center.id}/members`, form);
      toast.success(t("addMember")); setForm({ email: "", role: "employee", name: "", password: "", position: "" }); setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeRole = async (uid, role) => { await api.put(`/centers/${center.id}/members/${uid}`, { role }); load(); };
  const remove = async (uid) => { await api.delete(`/centers/${center.id}/members/${uid}`); load(); };

  if (!center) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4" />{center.name} · {t("members")}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg border border-border" data-testid="center-member">
              <UserAvatar name={m.name} src={m.profile_picture} size={36} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                <SelectTrigger className="w-32 h-8" data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">{t("employee")}</SelectItem>
                  <SelectItem value="supervisor">{t("supervisor")}</SelectItem>
                  <SelectItem value="admin">{t("admin")}</SelectItem>
                </SelectContent>
              </Select>
              <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid="remove-member"><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>

        {adding ? (
          <div className="space-y-2 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">{t("existingHint")}</p>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder={t("email")} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="member-email" />
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="new-member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">{t("employee")}</SelectItem>
                  <SelectItem value="supervisor">{t("supervisor")}</SelectItem>
                  <SelectItem value="admin">{t("admin")}</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder={`${t("fullName")} (${t("optional")})`} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <Input placeholder={`${t("password")} (${t("optional")})`} type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAdding(false)}>{t("cancel")}</Button>
              <Button size="sm" onClick={add} disabled={!form.email} data-testid="confirm-add-member">{t("add")}</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setAdding(true)} data-testid="add-member-button"><Plus className="w-4 h-4 mr-1.5" />{t("addMember")}</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Centers() {
  const { t } = useT();
  const { reload } = useCenter();
  const [centers, setCenters] = useState([]);
  const [counts, setCounts] = useState({});
  const [dialog, setDialog] = useState({ open: false, center: null });
  const [membersDlg, setMembersDlg] = useState({ open: false, center: null });

  const load = async () => {
    const { data } = await api.get("/centers");
    setCenters(data);
    reload();
    const c = {};
    await Promise.all(data.map(async (ct) => {
      try { const m = await api.get(`/centers/${ct.id}/members`); c[ct.id] = m.data.length; } catch { c[ct.id] = 0; }
    }));
    setCounts(c);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const del = async (id) => {
    if (!window.confirm(`${t("delete")} — ${t("confirm")}?`)) return;
    try { await api.delete(`/centers/${id}`); toast.success(t("delete")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleStatus = async (c) => {
    const next = (c.status || "active") === "active" ? "suspended" : "active";
    try { await api.post(`/centers/${c.id}/status?status=${next}`); toast.success(t(next)); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("centers")}</h1>
          <p className="text-muted-foreground mt-1">Multi-location management</p>
        </div>
        <Button onClick={() => setDialog({ open: true, center: null })} data-testid="add-center-button"><Plus className="w-4 h-4 mr-1.5" />{t("addCenter")}</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {centers.map((c, i) => (
          <Card key={c.id} className="p-5 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid="center-card">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${c.color}1a` }}>
                <Building2 className="w-6 h-6" style={{ color: c.color }} />
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, center: c })} data-testid="edit-center"><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)} data-testid="delete-center"><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
            <h3 className="font-display font-bold text-lg mt-3 flex items-center gap-2">{c.name}
              {c.type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-semibold">{c.type}</span>}
              {(c.status || "active") === "suspended" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">{t("suspended")}</span>}
            </h3>
            {c.address && <p className="text-sm text-muted-foreground">{c.address}</p>}
            {(c.contact_first_name || c.contact_last_name) && <p className="text-xs text-muted-foreground mt-1">{t("contactPerson")}: {`${c.contact_first_name || ""} ${c.contact_last_name || ""}`.trim()}</p>}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
              <Badge variant="secondary"><Users className="w-3 h-3 mr-1" />{counts[c.id] ?? "…"}</Badge>
              <Button variant="ghost" size="icon" onClick={() => toggleStatus(c)} title={(c.status || "active") === "active" ? t("suspend") : t("activate")} data-testid="toggle-center-status">
                {(c.status || "active") === "active" ? <PowerOff className="w-4 h-4 text-amber-600" /> : <Power className="w-4 h-4 text-emerald-600" />}
              </Button>
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => setMembersDlg({ open: true, center: c })} data-testid="manage-members">{t("members")}</Button>
            </div>
          </Card>
        ))}
      </div>

      <CenterDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} center={dialog.center} onSaved={load} />
      <MembersDialog open={membersDlg.open} onOpenChange={(o) => setMembersDlg((d) => ({ ...d, open: o }))} center={membersDlg.center} />
    </div>
  );
}
